import { AmendmentModel, assertNever } from '@signalvote/shared';
import { FilingLockCopy } from './filingLockCopy';
import { receivingOfficePhrase } from './receivingOffice';

/**
 * FIN-011 — turns the jurisdiction's canonical `AmendmentModel` (ADR-021
 * `filingProfile.amendmentModel`) into the treasurer-facing instruction that appears
 * after a successful file-report. Exhaustive switch with `assertNever` so any new model
 * value forces a copy decision here.
 *
 * ADR-021 — this reads the ONE ruleset-declared amendment axis (the backend stamps it on
 * the `X-Amendment-Model` response header), replacing the retired builder-declared
 * `AmendmentWorkflow` enum. Federal (`ElectronicResubmit`, an in-system whole-report
 * resubmit) is now distinguishable from LA/VA (`SelfContainedUpload`).
 *
 * The optional `stateCode` lets us surface state-specific portal names
 * (AL FCPA, VA COMET) when available. Fallback copy is state-neutral.
 *
 * `isEmailFiling` is the served email-transport verdict (`filingProfile.filingTransport === 'email'`,
 * surfaced as `usePortalUploadModel().isEmailFiling`). North Carolina is a self-contained-upload
 * jurisdiction that files by EMAIL to NCSBE (no portal), so when it is set the copy says "email the
 * corrected file to {NCSBE}" instead of the portal-upload wording — driven by the declared transport,
 * never a `stateCode === 'NC'` boolean. It overrides the model arm below because email transport only
 * ever co-occurs with `SelfContainedUpload` (a portal-edit or in-system model has no email variant).
 */
export function amendmentWorkflowHint(
  model: AmendmentModel,
  stateCode?: string | null,
  isEmailFiling = false,
  // The SERVED receiving-office name (`profile.portalName`); the stateCode fallback can't know MS's tier.
  officeName?: string,
): string {
  const portalName = portalNameFor(stateCode);
  if (isEmailFiling) {
    const office = receivingOfficePhrase(officeName ?? portalName);
    return `To file an amendment, regenerate this report after editing and email the corrected file to ${office}, then mark it filed.`;
  }
  switch (model) {
    case AmendmentModel.SelfContainedUpload:
      return 'To file an amendment, regenerate this report after editing and upload the new package.';
    case AmendmentModel.ElectronicResubmit:
      return 'To file an amendment, regenerate this report after editing and re-file the updated package.';
    case AmendmentModel.PortalOnly:
      return `Amendments must be edited directly in the ${portalName} — no package upload is required.`;
    default:
      return assertNever(model);
  }
}

/**
 * ADR-071 amendment (#575) — the jurisdiction-aware guidance on a boundary-straggler ledger row (an
 * Unattributed line that dates into an already-CLOSED period). It reads the SERVED `amendmentModel`
 * (ADR-021) and the served `portalName` — never a `stateCode === 'AL'` / `isAlabama` boolean or a
 * `FormatKind` re-derivation (AC5) — and frames the amend as the EXISTING per-jurisdiction path the
 * classify flow already routes into (AC6): classify the line to add it to its filed report (which
 * needs an amendment), or exclude it if it doesn't belong. `assertNever` forces a copy decision here
 * for any new model.
 *
 * `portalName` is the served `usePortalUploadModel().portalName` (#478/#481), so no "Alabama" / "FCPA
 * portal" is hard-coded. `isEmailFiling` is the served email-transport verdict (NC files by email to
 * the NCSBE, no portal); it overrides the model arm because email transport only co-occurs with
 * `SelfContainedUpload`.
 */
export function stragglerAmendHint(
  model: AmendmentModel | null,
  portalName: string,
  isEmailFiling = false,
): string {
  const lead = 'This transaction dates into a period you’ve already filed.';
  const orExclude = 'or exclude it if it doesn’t belong to your campaign.';
  if (isEmailFiling) {
    return `${lead} Classify it to add it to that report, then regenerate and email the corrected file to ${receivingOfficePhrase(portalName)} as an amendment — ${orExclude}`;
  }
  switch (model) {
    case AmendmentModel.PortalOnly:
      return `${lead} Classify it to record it, then add just this one transaction directly in the ${portalName} (it’s append-only, so no re-upload) — ${orExclude}`;
    case AmendmentModel.ElectronicResubmit:
      return `${lead} Classify it to add it to that report, then re-file the updated report as an amendment — ${orExclude}`;
    case AmendmentModel.SelfContainedUpload:
      return `${lead} Classify it to add it to that report, then regenerate and re-upload it as an amendment — ${orExclude}`;
    case null:
      return `${lead} Classify it to add it to that report — you’ll then file an amendment — ${orExclude}`;
    default:
      return assertNever(model);
  }
}

/**
 * §49 — the treasurer-facing nudge shown on an OPEN (still-editable) in-system
 * report that has already been generated/filed once. #571 — it is now driven by the SERVED
 * `amendmentModel` (ADR-021), not a `FormatKind`-derived workflow:
 *
 * - `PortalOnly` (append-only jurisdictions, e.g. AL FCPA): amendments are made in
 *   the portal, this app does NOT file amendments, and re-uploading the export
 *   would DOUBLE every row (the portal is append-only, ADR-020). Reuses the
 *   per-state portal name from {@link portalNameFor}.
 * - everything else (`ElectronicResubmit` federal, `SelfContainedUpload` LA/VA, or an
 *   unknown/`null` model): keeps the plain re-file framing
 *   (`FilingLockCopy.OpenAlreadyFiledNudge`).
 *
 * Routed through this helper (rather than a bare static string) so the portal
 * name composes per jurisdiction with no hard-coded "Alabama".
 */
export function openAlreadyFiledNudge(
  model: AmendmentModel | null,
  stateCode?: string | null,
): string {
  if (model === AmendmentModel.PortalOnly) {
    const portalName = portalNameFor(stateCode);
    return (
      `Already filed. Amendments are made in the ${portalName}, not here — this app `
      + `doesn't file amendments. Editing here won't change what's already on file, and `
      + `re-uploading the export would double every row (the ${portalName} is append-only). `
      + `To correct something already filed, amend it directly in the ${portalName}.`
    );
  }
  // ElectronicResubmit (federal in-system resubmit), SelfContainedUpload (LA/VA whole-report
  // re-upload), or an unknown model (profile not yet resolved) all keep the re-file framing.
  return FilingLockCopy.OpenAlreadyFiledNudge;
}

/**
 * Maps a report/preview's raw `stateCode` to its treasurer-facing portal name.
 *
 * #481 — this is NO LONGER the source of truth for the active campaign's portal name: that is the
 * SERVED `profile.portalName` (#478), consumed through `usePortalUploadModel.portalName`. This
 * switch survives only as a fallback for surfaces that hold a report/preview `stateCode` but not the
 * compliance profile (e.g. `PreviewReportButton`, `FilePreflightModal`, `ExportDeltaButton`) and for
 * the loading window. The email-transport verdict that used to live beside it (`isEmailFilingFor`)
 * was retired — it now crosses the wire as `profile.filingTransport === 'email'`.
 */
export function portalNameFor(stateCode: string | null | undefined): string {
  if (!stateCode) return 'state portal';
  switch (stateCode.toUpperCase()) {
    case 'AL': return 'FCPA portal';
    case 'VA': return 'COMET portal';
    case 'LA': return 'LEADERS portal';
    // #296 — NC has NO filing portal (the .cfd is emailed to the NCSBE inbox). Name the state
    // board (NCSBE), never the generic "state portal" fallthrough, on any surface that reads this.
    case 'NC': return 'NCSBE';
    // #860 — Mississippi has NO filing portal either (the PDF is emailed to a receiving office), and
    // that office is TIER-DEPENDENT — the Secretary of State for a state-level filer, the Circuit
    // Clerk for a county filer, with NO Secretary-of-State fallback for county. This stateCode-only
    // fallback can't know the tier, so it stays tier-neutral ("receiving office") rather than naming
    // the Secretary of State (which would be an SOS-in-copy leak for a county filer). It is only the
    // profile-loading fallback; the SERVED `profile.portalName` is authoritative and tier-resolved.
    case 'MS': return 'receiving office';
    default: return 'state portal';
  }
}
